"use client";

import * as React from "react";
import {
  BrainCircuit,
  Phone,
  Mail,
  FileText,
  ShieldQuestion,
  Code2,
  type LucideIcon,
} from "lucide-react";

import { toFa } from "@/lib/format";
import { pushView } from "@/stores/useAppStore";

type Settings = {
  siteName?: string | null;
  supportPhone?: string | null;
};

const DEFAULT_SITE_NAME = "آکادمی فَران";
const DEFAULT_PHONE = "09120000000";

const ANCHOR_LINKS: { href: string; label: string }[] = [
  { href: "#services", label: "خدمات" },
  { href: "#courses", label: "دوره‌ها" },
  { href: "#commission", label: "کمیسیون" },
];

function scrollToHash(href: string) {
  if (typeof window === "undefined") return;
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function faYear(): string {
  try {
    return new Intl.DateTimeFormat("fa-IR-u-ca-persian", { year: "numeric" })
      .format(new Date())
      .replace(/\s/g, "");
  } catch {
    return toFa("۱۴۰۳");
  }
}

type ActionLinkProps = {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
};

function ActionLink({ icon: Icon, label, onClick }: ActionLinkProps) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
      >
        <Icon className="size-4 text-primary" />
        {label}
      </button>
    </li>
  );
}

export function Footer() {
  const [settings, setSettings] = React.useState<Settings>({});

  React.useEffect(() => {
    let alive = true;
    fetch("/api/settings", { cache: "no-store" })
      .then((r) => r.json())
      .then((data: { settings?: Settings }) => {
        if (alive) setSettings(data.settings ?? {});
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      alive = false;
    };
  }, []);

  const siteName = settings.siteName?.trim() || DEFAULT_SITE_NAME;
  const supportPhone = settings.supportPhone?.trim() || DEFAULT_PHONE;
  const emailDomain = "academy-plus.ir";

  return (
    <footer className="mt-auto border-t border-border bg-card/50 backdrop-blur">
      <div className="mx-auto w-full max-w-7xl px-4 py-12">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {/* Brand */}
          <div className="col-span-2 space-y-3 md:col-span-1">
            <div className="flex items-center gap-2">
              <span className="flex size-9 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30">
                <BrainCircuit className="size-5" />
              </span>
              <span className="text-lg font-bold">{siteName}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              پلتفرم آموزش هوش مصنوعی، اکانت‌های رسمی و فرصت درآمدزایی از طریق
              سیستم کمیسیون — همراه شما در مسیر یادگیری و رشد.
            </p>
          </div>

          {/* Quick links */}
          <nav aria-label="دسترسی سریع" className="space-y-3">
            <h3 className="text-sm font-semibold">دسترسی سریع</h3>
            <ul className="space-y-2">
              {ANCHOR_LINKS.map((l) => (
                <li key={l.href}>
                  <button
                    type="button"
                    onClick={() => scrollToHash(l.href)}
                    className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    <FileText className="size-4 text-primary" />
                    {l.label}
                  </button>
                </li>
              ))}
              <ActionLink
                icon={Code2}
                label="سفارش پروژه"
                onClick={() => pushView("order-site")}
              />
            </ul>
          </nav>

          {/* Support */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">پشتیبانی</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Phone className="size-4 text-primary" />
                <span dir="ltr">{supportPhone}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="size-4 text-primary" />
                <span dir="ltr">support@{emailDomain}</span>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">قوانین</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <button
                  type="button"
                  onClick={() => scrollToHash("#faq")}
                  className="flex items-center gap-2 transition-colors hover:text-primary"
                >
                  <FileText className="size-4 text-primary" />
                  قوانین استفاده
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => scrollToHash("#faq")}
                  className="flex items-center gap-2 transition-colors hover:text-primary"
                >
                  <ShieldQuestion className="size-4 text-primary" />
                  حریم خصوصی
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-border/60 pt-6">
          <p className="text-center text-xs text-muted-foreground">
            © {faYear()} {siteName} — تمام حقوق محفوظ است.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
